import React, { useState } from 'react';
import PageLayout from '../../components/servicing/layout/PageLayout';
import { Link } from 'react-router-dom';
import { 
  WrenchScrewdriverIcon, 
  SparklesIcon, 
  CogIcon, 
  TruckIcon,
  PaintBrushIcon,
  RocketLaunchIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const Services = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const services = [
    {
      title: "Basic Service",
      description: "Includes general check-up, cleaning, and basic adjustments to keep your bike running smoothly.",
      path: "/services/basic",
      icon: WrenchScrewdriverIcon,
      color: "from-blue-500 to-cyan-500",
      features: ["Oil Change", "Brake Check", "Chain Lubrication"]
    },
    {
      title: "Premium Service",
      description: "Comprehensive servicing including brake adjustments, gear tuning, and drivetrain cleaning.",
      path: "/services/premium",
      icon: SparklesIcon,
      color: "from-purple-500 to-pink-500",
      features: ["Complete Tune-up", "Parts Replacement", "Performance Check"]
    },
    {
      title: "Repair Services",
      description: "Expert repairs for any bike issues, from flat tires to complex mechanical problems.",
      path: "/services/repairs",
      icon: CogIcon,
      color: "from-orange-500 to-red-500",
      features: ["Engine Repair", "Electrical Fix", "Emergency Service"]
    },
    {
      title: "Wheel & Tire Care",
      description: "Professional wheel alignment, tire replacement, and tube repairs.",
      path: "/services/wheels",
      icon: TruckIcon,
      color: "from-green-500 to-emerald-500",
      features: ["Balancing", "Alignment", "Tire Rotation"]
    },
    {
      title: "Detailing Services",
      description: "Deep cleaning and polishing to keep your bike looking brand new.",
      path: "/services/detailing",
      icon: PaintBrushIcon,
      color: "from-yellow-500 to-amber-500",
      features: ["Wash & Polish", "Paint Protection", "Interior Cleaning"]
    },
    {
      title: "Custom Upgrades",
      description: "Performance enhancements and personalized bike modifications.",
      path: "/services/upgrades",
      icon: RocketLaunchIcon,
      color: "from-indigo-500 to-purple-500",
      features: ["Performance Parts", "Custom Accessories", "Tech Upgrades"]
    }
  ];

  return (
    <PageLayout>
      <div className="responsive-padding bg-gradient-to-br from-gray-50 via-white to-indigo-50 min-h-screen">
        <div className="container mx-auto py-12 md:py-20">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center p-2 bg-indigo-100 rounded-full mb-6">
              <span className="px-4 py-1 bg-white rounded-full text-indigo-600 text-sm font-semibold shadow-sm">
                Professional Care
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Our Bike <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed">
              Expert maintenance and repair solutions to keep your bike performing at its best. 
              Choose from our comprehensive range of professional services.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.title}
                  to={service.path}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className="group relative"
                >
                  {/* Card */}
                  <div className="relative h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100">
                    {/* Gradient Accent */}
                    <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${service.color}`}></div>
                    
                    {/* Hover Gradient Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                    
                    <div className="relative p-8 space-y-6">
                      {/* Icon */}
                      <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.color} group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                        <Icon className="h-8 w-8 text-white" />
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-300">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-2">
                        {service.features.map((feature) => (
                          <li key={feature} className="flex items-center text-sm text-gray-700">
                            <span className={`mr-2 w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.color}`}></span>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <div className="pt-4 flex items-center justify-between text-indigo-600 font-semibold group-hover:text-indigo-700">
                        <span>Learn More</span>
                        <ArrowRightIcon className={`h-5 w-5 transition-transform duration-300 ${hoveredCard === index ? 'translate-x-2' : ''}`} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12 shadow-2xl">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Service Your Bike?
              </h2>
              <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
                Book your service today and experience professional care with guaranteed satisfaction
              </p>
              <Link 
                to="/book-service"
                className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                Book Now
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Services;
